import { getDefaultStore } from "jotai";
import { apiUrl } from "../lib/utils";
import { isLoggedIn, jwtAtom, logout, userIdentifyAtom } from "../store/user";
import type { components } from "./schema";

export interface IError {
  code: string;
  message: string;
}

const store = getDefaultStore();
// src/utils/request.ts
export async function request<TResponse>(
  input: RequestInfo,
  init?: RequestInit
): Promise<TResponse> {
  let token = store.get(jwtAtom);
  if (input !== "/auth/refresh") {
    await checkJwtExpiration(token);
    token = store.get(jwtAtom);
  } else {
    token = "";
  }

  const res = await fetch(`${apiUrl()}${input}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
      Authorization: token ? `Bearer ${token}` : "",
    },
  });

  if (res.status === 204) {
    return {} as TResponse;
  }

  if (!res.ok && res.status !== 409) {
    const error = await res.text();
    if (!error) {
      const defaultError: IError = {
        code: "unknown",
        message: "Request Error",
      };
      throw defaultError;
    }
    if (String(error).includes("USER_NOT_FOUND") || res.status === 401) {
      if (isLoggedIn()) {
        logout();
      }
    }

    // Try to parse error as JSON to extract code and message
    try {
      const errorObj = JSON.parse(error);
      if (
        errorObj &&
        typeof errorObj === "object" &&
        "code" in errorObj &&
        "message" in errorObj
      ) {
        const apiError: IError = {
          code: String(errorObj.code),
          message: String(errorObj.message),
        };
        throw apiError;
      }
      // If parsed but doesn't have code/message, use the parsed object's message or error
      const fallbackError: IError = {
        code: "unknown",
        message: errorObj.message || errorObj.error || String(error),
      };
      throw fallbackError;
    } catch (parseError) {
      // If already thrown our custom error format, re-throw it
      if (
        parseError &&
        typeof parseError === "object" &&
        "code" in parseError &&
        "message" in parseError
      ) {
        throw parseError as IError;
      }
      // If JSON parse failed or other error, use the original error string
      const defaultError: IError = { code: "unknown", message: String(error) };
      throw defaultError;
    }
  }

  if (res.json) {
    return res.json() as Promise<TResponse>;
  }
  return res.text() as Promise<TResponse>;
}

const checkJwtExpiration = async (token: string) => {
  if (!token) {
    return;
  }
  try {
    const payloadBase64 = token.split(".")[1]; // Get the middle part
    const payloadJson = atob(payloadBase64); // base64 decode
    const payload = JSON.parse(payloadJson); // Convert to object

    const now = new Date();

    if (payload.exp && payload.exp < now.getTime() / 1000) {
      // Expired, refresh
      await refreshJwt();
    } else if (payload.exp && payload.exp - now.getTime() / 1000 < 60 * 10) {
      // Expires within 10 minutes, refresh asynchronously
      refreshJwt();
    }

    // exp is a timestamp (in seconds)
  } catch (e) {
    console.error("Invalid JWT token", e);
    return null;
  }
};

type RefreshTokenResponse = components["schemas"]["RefreshTokenResponse"];

export const refreshJwt = async () => {
  const tokens = store.get(userIdentifyAtom)?.tokens;
  if (!tokens) {
    return;
  }
  const { refreshToken, refreshTokenExpiresAt } = tokens;

  if (refreshTokenExpiresAt) {
    const now = new Date();
    const expiresAt = new Date(refreshTokenExpiresAt);
    if (expiresAt.getTime() < now.getTime()) {
      store.set(userIdentifyAtom, null);
      return;
    }
  }

  const res = await request<RefreshTokenResponse>("/auth/refresh", {
    method: "POST",
    body: JSON.stringify({
      refreshToken: refreshToken,
    }),
  });

  if (res.tokens) {
    store.set(userIdentifyAtom, (old) => {
      if (!old) {
        return null;
      }
      return {
        ...old,
        ...res,
      };
    });
  }
};

// PalmID_SetConfig("allow_audit", "false");
// PalmID_Capture(...)
// 第二次扫手：
// PalmID_SetConfig("allow_audit", "true");
// PalmID_Capture(...)

// palms: {
//   /** Format: byte */
//   palmModel: string; //第二次
//   /** @description all models in this array should match palmModel. palmModel will be updated during the verification */
//   verifyPalmModels: string[]; //第一次
// }[];
